import { useMemo } from 'react';
import { Form, useFormState } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { GHG_DEFAULT_VALUE } from '@/config/declarationOptions';
import {
  buildGhgDefaultOptions,
  buildYesNoOptions,
  buildEscaBonusCapOptions,
} from '../../constants/editDeclarationOptions';
import { DeclarationTextField, DeclarationSelectField } from './declarationFields';
import styles from './EmissionDetailsStep.module.scss';

// The breakdown the declaration must carry when no RED III default value is applied.
const BREAKDOWN_FIELDS = Object.freeze([
  { name: 'eec', labelKey: 'ALLOCATIONS_FIELD_EEC' },
  { name: 'el', labelKey: 'ALLOCATIONS_FIELD_EL' },
  { name: 'ep', labelKey: 'ALLOCATIONS_FIELD_EP' },
  { name: 'etd', labelKey: 'ALLOCATIONS_FIELD_ETD' },
  { name: 'eu', labelKey: 'ALLOCATIONS_FIELD_EU' },
  { name: 'esca', labelKey: 'ALLOCATIONS_FIELD_ESCA' },
  { name: 'eccs', labelKey: 'ALLOCATIONS_FIELD_ECCS' },
  { name: 'eccr', labelKey: 'ALLOCATIONS_FIELD_ECCR' },
]);

const SAVING_FIELDS = Object.freeze([
  { name: 'savingTransport', labelKey: 'ALLOCATIONS_FIELD_SAVING_TRANSPORT' },
  { name: 'savingElectricity', labelKey: 'ALLOCATIONS_FIELD_SAVING_ELECTRICITY' },
  { name: 'savingElectricityOutermost', labelKey: 'ALLOCATIONS_FIELD_SAVING_OUTERMOST' },
  { name: 'savingUsefulHeat', labelKey: 'ALLOCATIONS_FIELD_SAVING_USEFUL_HEAT' },
  { name: 'savingHeatSubstitution', labelKey: 'ALLOCATIONS_FIELD_SAVING_SUBSTITUTION' },
]);

const ADD_ON_FIELDS = Object.freeze([
  { name: 'foodSecurityStandard', labelKey: 'FORM_FOOD_SECURITY_STANDARD' },
  { name: 'euDeforestationRegulation', labelKey: 'FORM_EU_DEFORESTATION_REGULATION' },
  { name: 'nonGmoFoodFeed', labelKey: 'FORM_NON_GMO_FOOD_FEED' },
  { name: 'nonGmoTechnicalMarkets', labelKey: 'FORM_NON_GMO_TECHNICAL_MARKETS' },
]);

// Step 3 — the GHG figures, their savings, and the optional add-on schemes.
export const EmissionDetailsStep = () => {
  const t = useTranslations();
  const { values } = useFormState();

  const ghgDefaultOptions = useMemo(() => buildGhgDefaultOptions(t), [t]);
  const yesNoOptions = useMemo(() => buildYesNoOptions(t), [t]);
  const escaBonusCapOptions = useMemo(() => buildEscaBonusCapOptions(t), [t]);

  // "No" is what asks for the eight-term breakdown; "Yes" keeps the single total.
  const needsBreakdown = values.defaultValueApplied === GHG_DEFAULT_VALUE.no;

  return (
    <>
      <Form.Row columns={2}>
        <Form.Section title={t('SECTION_GHG_EMISSION_DETAILS')}>
          <DeclarationSelectField
            name="defaultValueApplied"
            label={t('ALLOCATIONS_FIELD_DEFAULT_VALUE_APPLIED')}
            options={ghgDefaultOptions}
            required
          />
          <DeclarationTextField
            name="totalGhgEmissions"
            label={t('ALLOCATIONS_FIELD_TOTAL_GHG_EMISSIONS')}
            required
          />

          {needsBreakdown && (
            <>
              <Form.Row columns={2} className={styles.fieldsAlignEnd}>
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[0].name}
                  label={t(BREAKDOWN_FIELDS[0].labelKey)}
                />
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[1].name}
                  label={t(BREAKDOWN_FIELDS[1].labelKey)}
                />
              </Form.Row>
              <Form.Row columns={2} className={styles.fieldsAlignEnd}>
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[2].name}
                  label={t(BREAKDOWN_FIELDS[2].labelKey)}
                />
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[3].name}
                  label={t(BREAKDOWN_FIELDS[3].labelKey)}
                />
              </Form.Row>
              <Form.Row columns={2} className={styles.fieldsAlignEnd}>
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[4].name}
                  label={t(BREAKDOWN_FIELDS[4].labelKey)}
                />
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[5].name}
                  label={t(BREAKDOWN_FIELDS[5].labelKey)}
                />
              </Form.Row>
              <Form.Row columns={2} className={styles.fieldsAlignEnd}>
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[6].name}
                  label={t(BREAKDOWN_FIELDS[6].labelKey)}
                />
                <DeclarationTextField
                  name={BREAKDOWN_FIELDS[7].name}
                  label={t(BREAKDOWN_FIELDS[7].labelKey)}
                />
              </Form.Row>
            </>
          )}

          <Form.Row columns={2} className={styles.fieldsAlignEnd}>
            <DeclarationTextField
              name="allocatedHeat"
              label={t('ALLOCATIONS_FIELD_ALLOCATED_HEAT')}
            />
            <DeclarationTextField
              name="allocatedElectricity"
              label={t('ALLOCATIONS_FIELD_ALLOCATED_ELECTRICITY')}
            />
          </Form.Row>

          <DeclarationSelectField
            name="escaBonusCap"
            label={t('ALLOCATIONS_FIELD_ESCA_BONUS_CAP')}
            options={escaBonusCapOptions}
            required
          />
          <DeclarationTextField name="nuts2Area" label={t('ALLOCATIONS_FIELD_NUTS2_AREA')} />
        </Form.Section>

        <Form.Section title={t('SECTION_GHG_EMISSION_SAVING')}>
          {SAVING_FIELDS.map((field) => (
            <DeclarationTextField key={field.name} name={field.name} label={t(field.labelKey)} />
          ))}
        </Form.Section>
      </Form.Row>

      <Form.Section title={t('SECTION_VOLUNTARY_ADD_ONS')}>
        <Form.Row columns={2}>
          {ADD_ON_FIELDS.map((field) => (
            <DeclarationSelectField
              key={field.name}
              name={field.name}
              label={t(field.labelKey)}
              options={yesNoOptions}
            />
          ))}
        </Form.Row>
      </Form.Section>
    </>
  );
};
